// The «فلترة» dialog of the properties / requests lists: the system fields first (request type, category,
// property type, location, price / budget, features), then the company's own form questions (building status,
// streets, country of contact …). Long option lists are searchable pickers, not walls of chips.
// Every company sees only its own choices: the values that appear in its entries and the questions of its form.
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Autocomplete,
  Badge,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { collectConditions, type CompiledSchema, type FormField } from '@re/form-engine';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import { useLookups } from '../api/lookups';
import { useAuth } from '../auth/AuthContext';
import { getUiLanguage, tr } from '../lib/i18n';
import { Button } from './button';
import { TextField } from './text-field';

/** A filter on a form answer that has no system column (`attrs` query parameter). */
export interface AttrFilter {
  k: string;
  in?: string[];
  min?: number;
  max?: number;
  is?: boolean;
}

/** Everything the dialog can filter by. */
export interface ListFilters {
  requestTypeId?: number;
  categoryId?: number;
  propertyTypeId?: number;
  governorateId?: number;
  areaId?: number;
  priceMin?: number;
  priceMax?: number;
  featureIds?: number[];
  attrs?: AttrFilter[];
}
type Kind = 'OFFER' | 'DEMAND';
type Named = { id: number; name_ar: string; name_en?: string | null };

const FILTERABLE = new Set(['radio', 'dropdown', 'checkbox_group', 'boolean', 'number']);
/** more options than this → a searchable picker */
const CHIPS_MAX = 8;
const en = () => getUiLanguage() === 'en';
const text = (l?: { ar: string; en?: string } | null) => (en() && l?.en?.trim() ? l.en : l?.ar ?? '');
const nameOf = (x?: Named | null) => (!x ? '' : en() && x.name_en?.trim() ? x.name_en : x.name_ar);
const num = (s: string) => (s.trim() === '' || Number.isNaN(Number(s)) ? undefined : Number(s));

/** Number of active filters (the badge on the button). */
export const filtersCount = (f: ListFilters) =>
  [f.requestTypeId, f.categoryId, f.propertyTypeId, f.governorateId ?? f.areaId, f.priceMin ?? f.priceMax].filter((v) => v !== undefined).length +
  (f.featureIds?.length ?? 0) +
  (f.attrs?.length ?? 0);

/**
 * The list's filters, kept until the employee clears them: opening an entry, going to another page or reloading
 * comes back to the same filtered list. Saved in this browser, per employee and per list.
 */
export function useSavedFilters(kind: Kind): [ListFilters, (v: ListFilters) => void] {
  const { me } = useAuth();
  const key = `formtags:filters:${kind}:${me?.user.id ?? ''}`;
  const read = (k: string): ListFilters => {
    try {
      const v = JSON.parse(localStorage.getItem(k) ?? '{}') as unknown;
      return v && typeof v === 'object' && !Array.isArray(v) ? (v as ListFilters) : {};
    } catch {
      return {};
    }
  };
  const [state, setState] = useState<{ key: string; value: ListFilters }>(() => ({ key, value: read(key) }));
  useEffect(() => {
    if (state.key !== key) setState({ key, value: read(key) });
  }, [key, state.key]);
  const set = useCallback(
    (v: ListFilters) => {
      setState({ key, value: v });
      try {
        if (filtersCount(v)) localStorage.setItem(key, JSON.stringify(v));
        else localStorage.removeItem(key);
      } catch {
        /* storage blocked: keep it for this visit */
      }
    },
    [key],
  );
  return [state.key === key ? state.value : read(key), set];
}

/** The query parameters of the list endpoints. */
export function filtersQuery(f: ListFilters): Record<string, string | number> {
  const q: Record<string, string | number> = {};
  for (const k of ['requestTypeId', 'categoryId', 'propertyTypeId', 'governorateId', 'areaId', 'priceMin', 'priceMax'] as const) {
    if (f[k] !== undefined) q[k] = f[k]!;
  }
  if (f.featureIds?.length) q.featureIds = f.featureIds.join(',');
  if (f.attrs?.length) q.attrs = JSON.stringify(f.attrs);
  return q;
}

interface FilterOptionsResponse {
  requestTypeIds: number[];
  categoryIds: number[];
  propertyTypeIds: number[];
  featureIds: number[];
  governorates: Named[];
  areas: (Named & { governorate_id: number })[];
  fields: CompiledSchema['fields'];
}

/** What this company's list can be filtered by (values used in its entries + its form questions). */
function useFilterOptions(kind: Kind) {
  return useQuery({
    queryKey: ['filter-options', kind],
    queryFn: () => api<FilterOptionsResponse>(kind === 'OFFER' ? '/properties/filter-options' : '/requests/filter-options'),
    staleTime: 30_000,
  });
}

/** Questions of the published form that can filter this list: custom answers that apply to this kind of entry. */
function useFilterFields(kind: Kind) {
  const form = useFilterOptions(kind);
  const lookups = useLookups();
  return useMemo(() => {
    const fields = form.data?.fields ?? [];
    const rtField = fields.find((f) => f.type === 'request_type');
    const directions = kind === 'OFFER' ? ['OFFER'] : ['DEMAND', 'SERVICE'];
    const codes = new Set((lookups.data?.requestTypes ?? []).filter((t) => directions.includes(t.direction)).map((t) => t.code));
    const applies = (f: FormField) => {
      const rt = [...collectConditions(f.visibleWhen ?? null)].filter((c) => c.field === rtField?.key && (c.op === 'eq' || c.op === 'in'));
      if (!rt.length) return true;
      return rt.some((c) => (Array.isArray(c.value) ? c.value : [c.value]).some((v) => codes.has(String(v))));
    };
    return [...fields].sort((a, b) => a.order - b.order).filter((f) => !f.mapsTo && FILTERABLE.has(f.type) && applies(f));
  }, [form.data, lookups.data, kind]);
}

/** Governorates used in this list, and their areas used in it. */
function usePlaces(kind: Kind, governorateId?: number) {
  const o = useFilterOptions(kind).data;
  return { govs: o?.governorates ?? [], areas: (o?.areas ?? []).filter((a) => a.governorate_id === governorateId) };
}

/** System pickers: request types for this list, categories, types of the chosen category, features of the chosen type. */
function useOptions(kind: Kind, f: ListFilters) {
  const lookups = useLookups().data;
  const used = useFilterOptions(kind).data;
  return useMemo(() => {
    const directions = kind === 'OFFER' ? ['OFFER'] : ['DEMAND', 'SERVICE'];
    const has = (list: number[] | undefined) => {
      const set = new Set(list ?? []);
      return (x: { id: number }) => set.has(x.id);
    };
    const requestTypes = (lookups?.requestTypes ?? []).filter((t) => directions.includes(t.direction)).filter(has(used?.requestTypeIds));
    const categories = (lookups?.categories ?? []).filter(has(used?.categoryIds));
    const propertyTypes = (lookups?.propertyTypes ?? []).filter(has(used?.propertyTypeIds)).filter((t) => !f.categoryId || t.category_id === f.categoryId);
    const typeIds = f.propertyTypeId ? [f.propertyTypeId] : f.categoryId ? propertyTypes.map((t) => t.id) : null;
    const groups = new Map((lookups?.featureGroups ?? []).map((g, i) => [g.id, { i, name: nameOf(g) }]));
    const usedFeatures = new Set(used?.featureIds ?? []);
    const features = (lookups?.features ?? [])
      .filter((x) => usedFeatures.has(x.id) && (!x.parent_id || !usedFeatures.has(x.parent_id)))
      .filter((x) => (!typeIds || x.property_type_ids.length === 0 || x.property_type_ids.some((id) => typeIds.includes(id))))
      .map((x) => ({ ...x, group: groups.get(x.group_id ?? -1)?.name ?? tr('أخرى', 'Other'), gi: groups.get(x.group_id ?? -1)?.i ?? 999 }))
      .sort((a, b) => a.gi - b.gi);
    return { requestTypes, categories, propertyTypes, features, all: lookups };
  }, [lookups, used, kind, f.categoryId, f.propertyTypeId]);
}

/** A searchable single choice. */
function Pick<T extends Named>({ label, options, value, onChange, disabled }: { label: string; options: T[]; value?: number; onChange: (id?: number) => void; disabled?: boolean }) {
  const cur = options.find((o) => o.id === value) ?? null;
  return (
    <Autocomplete
      size="small"
      options={options}
      value={cur}
      disabled={disabled}
      getOptionLabel={nameOf}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, v) => onChange(v?.id)}
      renderInput={(p) => <TextField {...p} label={label} placeholder={tr('الكل', 'All')} />}
      noOptionsText={tr('لا نتائج', 'No results')}
    />
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>{title}</Typography>
      {children}
    </Box>
  );
}

/** «فلترة» button + dialog. `value` / `onChange` hold the active filters. */
export function FormFiltersButton({ kind, value, onChange }: { kind: Kind; value: ListFilters; onChange: (v: ListFilters) => void }) {
  const fields = useFilterFields(kind);
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [d, setD] = useState<ListFilters>({});
  const opts = useOptions(kind, d);
  const places = usePlaces(kind, d.governorateId);
  const count = filtersCount(value);
  const draftCount = filtersCount(d);

  const set = (patch: Partial<ListFilters>) => setD((cur) => ({ ...cur, ...patch }));
  const attrOf = (k: string) => d.attrs?.find((a) => a.k === k);
  const putAttr = (k: string, patch: Partial<AttrFilter> | null) =>
    setD((cur) => {
      const rest = (cur.attrs ?? []).filter((a) => a.k !== k);
      const merged = { ...(cur.attrs?.find((a) => a.k === k) ?? { k }), ...patch } as AttrFilter;
      const clean = Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined && !(Array.isArray(v) && !v.length))) as unknown as AttrFilter;
      const empty = !patch || (!clean.in && clean.min === undefined && clean.max === undefined && clean.is === undefined);
      return { ...cur, attrs: empty ? rest : [...rest, clean] };
    });

  const start = () => {
    setD(value);
    setOpen(true);
  };
  const apply = () => {
    onChange(d);
    setOpen(false);
  };
  const isOffer = kind === 'OFFER';
  // a choice shows when there is something to choose (or it is already set): one request type = nothing to filter
  const show = (() => {
    const rt = opts.requestTypes.length > 1 || d.requestTypeId !== undefined;
    const cat = opts.categories.length > 1 || d.categoryId !== undefined;
    const pt = opts.propertyTypes.length > 1 || d.propertyTypeId !== undefined;
    return {
      rt, cat, pt,
      propertyCols: [rt, cat, pt].filter(Boolean).length,
      location: places.govs.length > 0 || d.governorateId !== undefined,
      features: opts.features.length > 0 || !!d.featureIds?.length,
    };
  })();

  return (
    <>
      <Badge color="primary" badgeContent={count} invisible={!count}>
        <Button variant="outlined" startIcon={<FilterListIcon />} onClick={start}>{tr('فلترة', 'Filters')}</Button>
      </Badge>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth fullScreen={small} scroll="paper">
        <DialogTitle>{isOffer ? tr('فلترة العقارات', 'Filter properties') : tr('فلترة الطلبات', 'Filter requests')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} divider={<Divider flexItem />}>
            {show.propertyCols > 0 && <Section title={tr('العقار', 'Property')}>
              <Grid container spacing={1.5}>
                {show.rt && <Grid size={{ xs: 12, sm: 12 / show.propertyCols }}>
                  <Pick label={tr('نوع الطلب', 'Request type')} options={opts.requestTypes} value={d.requestTypeId} onChange={(id) => set({ requestTypeId: id })} />
                </Grid>}
                {show.cat && <Grid size={{ xs: 12, sm: 12 / show.propertyCols }}>
                  <Pick
                    label={tr('الفئة', 'Category')}
                    options={opts.categories}
                    value={d.categoryId}
                    onChange={(id) => {
                      const keep = id && opts.all?.propertyTypes.find((t) => t.id === d.propertyTypeId)?.category_id === id;
                      set({ categoryId: id, propertyTypeId: keep ? d.propertyTypeId : undefined });
                    }}
                  />
                </Grid>}
                {show.pt && <Grid size={{ xs: 12, sm: 12 / show.propertyCols }}>
                  <Pick
                    label={tr('نوع العقار', 'Property type')}
                    options={opts.propertyTypes}
                    value={d.propertyTypeId}
                    onChange={(id) => {
                      const t = opts.all?.propertyTypes.find((x) => x.id === id);
                      set({ propertyTypeId: id, categoryId: t ? t.category_id : d.categoryId });
                    }}
                  />
                </Grid>}
              </Grid>
            </Section>}

            {show.location && <Section title={tr('الموقع', 'Location')}>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Pick label={tr('المحافظة', 'Governorate')} options={places.govs} value={d.governorateId} onChange={(id) => set({ governorateId: id, areaId: undefined })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Pick label={tr('المنطقة', 'Area')} options={places.areas} value={d.areaId} disabled={!d.governorateId} onChange={(id) => set({ areaId: id })} />
                </Grid>
              </Grid>
            </Section>}

            <Section title={isOffer ? tr('السعر', 'Price') : tr('الميزانية', 'Budget')}>
              <Grid container spacing={1.5}>
                <Grid size={6}>
                  <TextField fullWidth size="small" type="number" label={tr('من', 'From')} value={d.priceMin ?? ''} onChange={(e) => set({ priceMin: num(e.target.value) })} slotProps={{ htmlInput: { min: 0, dir: 'ltr' } }} />
                </Grid>
                <Grid size={6}>
                  <TextField fullWidth size="small" type="number" label={tr('إلى', 'To')} value={d.priceMax ?? ''} onChange={(e) => set({ priceMax: num(e.target.value) })} slotProps={{ htmlInput: { min: 0, dir: 'ltr' } }} />
                </Grid>
              </Grid>
            </Section>

            {show.features && <Section title={tr('الميزات', 'Features')}>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={opts.features}
                groupBy={(o) => o.group}
                getOptionLabel={(o) => nameOf(o)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={opts.features.filter((x) => d.featureIds?.includes(x.id))}
                onChange={(_, v) => set({ featureIds: v.map((x) => x.id) })}
                renderInput={(p) => <TextField {...p} placeholder={d.featureIds?.length ? '' : tr('ابحث عن ميزة: مصعد، موقف، تكييف…', 'Search a feature: elevator, parking…')} />}
                noOptionsText={tr('لا نتائج', 'No results')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {isOffer ? tr('تظهر العقارات التي فيها كل الميزات المختارة.', 'Shows properties that have all the chosen features.') : tr('تظهر الطلبات التي تطلب كل الميزات المختارة.', 'Shows requests that ask for all the chosen features.')}
              </Typography>
            </Section>}

            {fields.length > 0 && (
              <Section title={tr('أسئلة النموذج', 'Form questions')}>
                <Stack spacing={2}>
                  {fields.map((f) => {
                    const cur = attrOf(f.key);
                    const options = f.config.options ?? [];
                    return (
                      <Box key={f.key}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{text(f.label)}</Typography>
                        {f.type === 'number' ? (
                          <Grid container spacing={1.5}>
                            <Grid size={6}><TextField fullWidth size="small" type="number" label={tr('من', 'From')} value={cur?.min ?? ''} onChange={(e) => putAttr(f.key, { min: num(e.target.value) })} /></Grid>
                            <Grid size={6}><TextField fullWidth size="small" type="number" label={tr('إلى', 'To')} value={cur?.max ?? ''} onChange={(e) => putAttr(f.key, { max: num(e.target.value) })} /></Grid>
                          </Grid>
                        ) : f.type === 'boolean' ? (
                          <ToggleButtonGroup size="small" exclusive value={cur?.is ?? null} onChange={(_, v: boolean | null) => putAttr(f.key, v === null ? null : { is: v })}>
                            <ToggleButton value={true}>{tr('نعم', 'Yes')}</ToggleButton>
                            <ToggleButton value={false}>{tr('لا', 'No')}</ToggleButton>
                          </ToggleButtonGroup>
                        ) : options.length > CHIPS_MAX ? (
                          <Autocomplete
                            multiple
                            size="small"
                            disableCloseOnSelect
                            options={options}
                            getOptionLabel={(o) => text(o.label)}
                            isOptionEqualToValue={(a, b) => a.value === b.value}
                            value={options.filter((o) => cur?.in?.includes(o.value))}
                            onChange={(_, v) => putAttr(f.key, { in: v.map((o) => o.value) })}
                            renderInput={(p) => <TextField {...p} placeholder={cur?.in?.length ? '' : tr('ابحث واختر…', 'Search and choose…')} />}
                            noOptionsText={tr('لا نتائج', 'No results')}
                          />
                        ) : (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {options.map((o) => {
                              const on = cur?.in?.includes(o.value) ?? false;
                              return (
                                <Chip
                                  key={o.value}
                                  label={text(o.label)}
                                  color={on ? 'primary' : 'default'}
                                  variant={on ? 'filled' : 'outlined'}
                                  onClick={() => putAttr(f.key, { in: on ? cur!.in!.filter((x) => x !== o.value) : [...(cur?.in ?? []), o.value] })}
                                />
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Section>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={!draftCount} onClick={() => setD({})}>{tr('مسح الكل', 'Clear all')}</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setOpen(false)}>{tr('إلغاء', 'Cancel')}</Button>
          <Button variant="contained" onClick={apply}>{tr('تطبيق', 'Apply')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** The active filters as removable chips, shown above the list. */
export function ActiveFormFilters({ kind, value, onChange }: { kind: Kind; value: ListFilters; onChange: (v: ListFilters) => void }) {
  const fields = useFilterFields(kind);
  const opts = useOptions(kind, {});
  const places = usePlaces(kind, value.governorateId);
  if (!filtersCount(value)) return null;
  const all = opts.all;
  const chips: { key: string; label: string; clear: ListFilters }[] = [];
  const by = <T extends Named>(list: T[] | undefined, id?: number) => nameOf(list?.find((x) => x.id === id));
  if (value.requestTypeId) chips.push({ key: 'rt', label: by(all?.requestTypes, value.requestTypeId), clear: { ...value, requestTypeId: undefined } });
  if (value.categoryId) chips.push({ key: 'cat', label: by(all?.categories, value.categoryId), clear: { ...value, categoryId: undefined, propertyTypeId: undefined } });
  if (value.propertyTypeId) chips.push({ key: 'pt', label: by(all?.propertyTypes, value.propertyTypeId), clear: { ...value, propertyTypeId: undefined } });
  if (value.governorateId) {
    const area = value.areaId ? by(places.areas, value.areaId) : '';
    chips.push({ key: 'loc', label: [by(places.govs, value.governorateId), area].filter(Boolean).join(' / '), clear: { ...value, governorateId: undefined, areaId: undefined } });
  }
  if (value.priceMin !== undefined || value.priceMax !== undefined) {
    const name = kind === 'OFFER' ? tr('السعر', 'Price') : tr('الميزانية', 'Budget');
    const n = (v: number) => v.toLocaleString(en() ? 'en' : 'ar-JO');
    const label = value.priceMin !== undefined && value.priceMax !== undefined ? `${name}: ${n(value.priceMin)} – ${n(value.priceMax)}` : value.priceMin !== undefined ? `${name} ≥ ${n(value.priceMin)}` : `${name} ≤ ${n(value.priceMax!)}`;
    chips.push({ key: 'price', label, clear: { ...value, priceMin: undefined, priceMax: undefined } });
  }
  for (const id of value.featureIds ?? []) chips.push({ key: `f${id}`, label: by(all?.features, id), clear: { ...value, featureIds: value.featureIds!.filter((x) => x !== id) } });
  for (const f of value.attrs ?? []) {
    const field = fields.find((x) => x.key === f.k);
    const name = field ? text(field.label) : f.k;
    const label = f.in
      ? `${name}: ${f.in.map((v) => text(field?.config.options?.find((o) => o.value === v)?.label) || v).join(tr('، ', ', '))}`
      : f.is !== undefined
      ? `${name}: ${f.is ? tr('نعم', 'Yes') : tr('لا', 'No')}`
      : f.min !== undefined && f.max !== undefined
      ? `${name}: ${f.min} – ${f.max}`
      : f.min !== undefined
      ? `${name} ≥ ${f.min}`
      : `${name} ≤ ${f.max}`;
    chips.push({ key: `a:${f.k}`, label, clear: { ...value, attrs: value.attrs!.filter((x) => x.k !== f.k) } });
  }
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
      {chips.map((c) => <Chip key={c.key} size="small" label={c.label || '…'} onDelete={() => onChange(c.clear)} />)}
      <Chip size="small" variant="outlined" label={tr('مسح الفلاتر', 'Clear filters')} onClick={() => onChange({})} />
    </Stack>
  );
}
